/**
 * Cloudinary direct-upload helpers (frontend → Cloudinary CDN).
 *
 * Flow:
 *   1. Request a signed signature from our backend (admin-auth required).
 *   2. POST the file straight to Cloudinary's edge endpoint with that signature.
 *   3. Receive { secure_url, public_id, duration, width, height, ... } back.
 *
 * This avoids streaming large files through the FastAPI worker.
 *
 * TEMPORARY: Until valid Cloudinary credentials are provided, images use a
 * base64 fallback (`uploadImageAsBase64`) — same admin UX, no CDN/auto-WebP.
 * Video uploads remain Cloudinary-only and are disabled in the admin UI.
 */
import api from "./api";

/**
 * Feature flag — flip to `true` once a valid Cloudinary cloud_name is configured
 * on the backend (`CLOUDINARY_CLOUD_NAME` in `/app/backend/.env`).
 *
 * When `false`:
 *   - Image uploads fall back to in-browser base64 encoding (no CDN/auto-WebP).
 *   - Video uploads are disabled in the admin UI (the YouTube link flow still works).
 *
 * Backend `/api/admin/cloudinary/signature` will keep returning 200 either way,
 * but actual Cloudinary uploads will fail until credentials are valid.
 */
export const CLOUDINARY_ENABLED = true;

// Client-side guardrails (server still validates folder & admin auth).
export const IMAGE_MAX_BYTES = 12 * 1024 * 1024; // 12 MB per image
export const VIDEO_MAX_BYTES = 60 * 1024 * 1024; // 60 MB per clip
export const VIDEO_MAX_SECONDS = 20;
export const VIDEO_MIN_SECONDS = 2; // sanity
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"]; // mov→quicktime

/**
 * Read a video file's duration locally (before upload) using an off-DOM <video> element.
 * Returns duration in seconds (Number).
 */
export const probeVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.src = url;
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(d)) return reject(new Error("Could not read video duration"));
      resolve(d);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video metadata"));
    };
  });

/**
 * Read a file → base64 data URL. Used as fallback when Cloudinary is disabled.
 */
const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read image file"));
    reader.readAsDataURL(file);
  });

/**
 * Fallback image "upload" — encodes to base64 and stores inline. No CDN, no auto-WebP.
 * Returns the same shape as Cloudinary so the caller is interchangeable.
 */
export const uploadImageAsBase64 = async (file) => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`Unsupported image type: ${file.type}. Use JPG, PNG or WebP.`);
  }
  // Tighter cap for base64 — these end up in MongoDB documents.
  const MAX_BASE64_BYTES = 4 * 1024 * 1024;
  if (file.size > MAX_BASE64_BYTES) {
    throw new Error(
      `Image too large for inline storage (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 4 MB while CDN is offline.`
    );
  }
  const dataUrl = await readAsDataUrl(file);
  return {
    secure_url: dataUrl,
    public_id: null, // null indicates "not a Cloudinary asset"
    width: null,
    height: null,
    format: file.type.split("/")[1] || "",
    bytes: file.size,
  };
};

/**
 * Upload an image directly to Cloudinary using a backend-issued signature.
 * @returns {Promise<{secure_url, public_id, width, height, format, bytes}>}
 */
export const uploadImageToCloudinary = async (file, { folder = "opikjewels/gemstones", onProgress } = {}) => {
  // While Cloudinary is disabled, transparently fall back to base64 storage.
  if (!CLOUDINARY_ENABLED) {
    if (typeof onProgress === "function") onProgress(50);
    const result = await uploadImageAsBase64(file);
    if (typeof onProgress === "function") onProgress(100);
    return result;
  }
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`Unsupported image type: ${file.type}. Use JPG, PNG or WebP.`);
  }
  if (file.size > IMAGE_MAX_BYTES) {
    throw new Error(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 12 MB.`);
  }
  const { data: sig } = await api.get(`/admin/cloudinary/signature?resource_type=image&folder=${encodeURIComponent(folder)}`);
  return _upload(file, sig, "image", onProgress);
};

/**
 * Upload a video directly to Cloudinary. Enforces duration & size client-side.
 * @returns {Promise<{secure_url, public_id, duration, width, height, format, bytes}>}
 */
export const uploadVideoToCloudinary = async (file, { folder = "opikjewels/gemstones", onProgress } = {}) => {
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    throw new Error(`Unsupported video format: ${file.type}. Use MP4, MOV or WebM.`);
  }
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error(`Video too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 60 MB.`);
  }
  const duration = await probeVideoDuration(file);
  if (duration > VIDEO_MAX_SECONDS) {
    throw new Error(`Video too long (${duration.toFixed(1)}s). Max ${VIDEO_MAX_SECONDS}s.`);
  }
  if (duration < VIDEO_MIN_SECONDS) {
    throw new Error(`Video too short (${duration.toFixed(1)}s). Min ${VIDEO_MIN_SECONDS}s.`);
  }
  const { data: sig } = await api.get(`/admin/cloudinary/signature?resource_type=video&folder=${encodeURIComponent(folder)}`);
  return _upload(file, sig, "video", onProgress);
};

/** Internal — POST file to Cloudinary's upload endpoint with the signed params. */
const _upload = (file, sig, kind, onProgress) =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);

    const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${kind}/upload`;
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);

    if (typeof onProgress === "function") {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data?.error?.message || `Upload failed (${xhr.status})`));
      } catch (err) {
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(form);
  });

/**
 * Delete a Cloudinary asset by public_id (admin-only on backend).
 */
export const deleteFromCloudinary = (public_id, resource_type = "image") =>
  api.post("/admin/cloudinary/delete", { public_id, resource_type }).then((r) => r.data);

/**
 * Parse a YouTube URL → { id, watch_url, embed_url, thumbnail_url }.
 * Accepts: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/shorts/
 */
export const parseYouTubeUrl = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const url = raw.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    /^([A-Za-z0-9_-]{11})$/, // bare video id
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) {
      const id = m[1];
      return {
        id,
        watch_url: `https://www.youtube.com/watch?v=${id}`,
        // nocookie + rel=0 + modestbranding kills "related videos" suggestions per request
        embed_url: `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`,
        thumbnail_url: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      };
    }
  }
  return null;
};

/**
 * Build a Cloudinary CDN URL with transformation params.
 * Example: cdnImage(url, "w_800,q_auto,f_auto")
 * Safely returns the original URL when it isn't a Cloudinary asset (e.g. base64, Unsplash, etc.).
 */
export const cdnTransform = (cloudinaryUrl, transform) => {
  if (!cloudinaryUrl || typeof cloudinaryUrl !== "string") return cloudinaryUrl;
  if (!cloudinaryUrl.includes("res.cloudinary.com") || !cloudinaryUrl.includes("/upload/")) return cloudinaryUrl;
  return cloudinaryUrl.replace("/upload/", `/upload/${transform}/`);
};
